import { CrossfireLevel } from './CrossfireLevel.js';

/**
 * "Dust II" — a reconstruction of the Counter-Strike bomb-defusal map.
 *
 * Converted from the compiled GoldSrc BSP by the same pipeline as Assault:
 * exact face geometry and real texture axes out of the lump data, and colour
 * measured from the palettes the BSP embeds (3845 faces resolved this way).
 * Nothing of Valve's art is redistributed — geometry plus an average RGB per
 * texture name, rendered with our own procedural PBR surfaces.
 *
 * The whole level implementation is shared with CrossfireLevel; only the
 * payload and the lighting mood differ.
 */
export class Dust2Level extends CrossfireLevel {
  static displayName = 'DUST II';
  static description = 'Counter-Strike · 1999';
  static mapFile = 'dust2.json';

  /**
   * Hard, high desert sun through dust haze — the opposite end of the range
   * from Assault's overcast night, and derived the same way, by sampling the
   * skybox the map shipped with (`gfx/env/des*.tga`):
   *
   *   desup  all #c0d5e3  top #9eacc4   meanLum 0.821
   *   desft  all #9f9787  top #acbfd4   meanLum 0.595   bottom #a18759
   *   desdn  all #ab9066  top #b59970   meanLum 0.576
   *
   * So: a pale desaturated blue overhead rather than deep sky (that is haze,
   * and it is why Dust reads as hot), a sun high enough to keep shadows short
   * and hard, and a warm sand bounce standing in for the light coming back off
   * every surface in a map made entirely of sandstone. Turbidity is well up
   * from the default for the same reason.
   */
  static atmosphere = {
    elevation: 58, azimuth: 145, turbidity: 5.2,
    rayleigh: 1.4,                       // haze, not deep blue
    mie: 0.006, mieG: 0.94, skyTrim: 0.62,
    sunColor: 0xfff2d4, sunIntensity: 15.5,
    hemiSky: 0xacbfd4, hemiGround: 0xc09a66, hemiIntensity: 1.05,
    bounceColor: 0xe0c092, bounceIntensity: 0.46,
    envIntensity: 0.52, vmEnvIntensity: 0.55,
    // Brightness now comes from the light rig, not from clipping albedo. The
    // converter used to push sandstone to near-white, which read bright but
    // washed all the colour out of it; the albedo is honest now, so the key and
    // the exposure carry the load and the sand keeps its chroma.
    exposure: 0.60,
    // Thin, high, warm cloud — the desert skybox is hazy rather than cloudy, so
    // this is a sparse high deck that softens the zenith without ever shading
    // the map. High altitude keeps the cells large and slow.
    clouds: {
      coverage: 0.30, density: 0.80, altitude: 1500, scale: 0.0011,
      wind: [1, 0.25], speed: 0.003,
      tint: 0xfff6e8, shade: 0xb9b2ae, detail: 0.95, opacity: 0.85,
    },
  };
}
